/**
 * Wrapper around generated RTK Query API with cleaner naming and custom logic.
 *
 * All endpoint references (keys, hooks, endpoint map) are derived from the
 * central definitions in `./config.ts` so generated names live in one place.
 */
import { generatedApi } from './api.generated';
import {
  createEndpointExports,
  createHookExports,
  provideListTags,
  LIST_TAG_ID,
} from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import type { BaseQueryFn, TypedMutationOnQueryStarted } from '@reduxjs/toolkit/query';
import { SMM_API_ENDPOINTS as cfg } from './config';
import { getSmmError, isSmmStatusRefused } from './errors';
import { selectSocialPosts } from './list-adapter';
import type { GetSocialPostResponse } from './types.generated';

/** Tag of one post by id, shared by every mutation that changes that post. */
const postTag = (id: number) => ({ type: 'SocialPost' as const, id });
const POST_LIST_TAG = { type: 'SocialPost' as const, id: LIST_TAG_ID };

/** Invalidates the post itself and the list, since status/media drive both. */
const invalidatePostAndList = (_result: unknown, _error: unknown, arg: { id: number }) => [
  postTag(arg.id),
  POST_LIST_TAG,
];

/**
 * Writes the post a status mutation answers with into the post's own cache
 * entry as soon as it lands, so the page shows the new status (and its
 * actions) straight away instead of the old one until the invalidation's
 * refetch is back. The refetch still runs and has the last word.
 *
 * Every such mutation answers with the same post schema as `getSocialPost`,
 * generated once per endpoint; passing the response here is what would report
 * a future divergence, at the endpoint that diverged.
 */
const upsertPost = (id: number, post: GetSocialPostResponse) =>
  generatedApi.util.upsertQueryData(cfg.getSocialPost.generated, { id }, post);

/** RTK's lifecycle API of a mutation keyed by a post id that answers with `TPost`. */
type PostMutationLifecycle<TPost> = Parameters<
  NonNullable<TypedMutationOnQueryStarted<TPost, { id: number }, BaseQueryFn>>
>[1];

/**
 * `onQueryStarted` of the status mutations that answer with the post: writes
 * that post into its cache entry once the mutation succeeds. A failed one
 * writes nothing: that rejection is the mutation's own, handled by whoever
 * awaited it.
 *
 * Typed by RTK's own lifecycle API ({@link PostMutationLifecycle}), generic over the response,
 * so each endpoint still gets its own `queryFulfilled` type and RTK's real
 * `dispatch` (a hand-written narrower lifecycle type once hid a floating
 * `dispatch` promise from the linter). `TPost` has to be a post: an endpoint
 * whose response drifts from `getSocialPost`'s fails to compile where it
 * passes this.
 */
const upsertPostOnSettled = async <TPost extends GetSocialPostResponse>(
  { id }: { id: number },
  { dispatch, queryFulfilled }: PostMutationLifecycle<TPost>,
): Promise<void> => {
  let post: TPost;
  try {
    ({ data: post } = await queryFulfilled);
  } catch {
    return;
  }
  await dispatch(upsertPost(id, post));
};

/**
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * - `SocialPost` — per-post `{ id }` tags plus `{ id: 'LIST' }` on the list.
 *   Every post mutation invalidates the post and the list: status changes
 *   (approve/unschedule/redraft/cancel/unpublish) move the post between list filters, so the
 *   per-item tag alone is not enough for a filtered list that no longer (or
 *   not yet) contains it.
 * - Delete only invalidates the list: the post is gone, refetching its
 *   `{ id }` would just 404.
 * - Presign changes nothing server-side and invalidates nothing, so an open
 *   post view is not refetched in the middle of an upload — except when the
 *   policy refuses it for the post's status ({@link isSmmStatusRefused}): the
 *   post stopped being a draft meanwhile, and only a re-read says so; or the
 *   post is gone (404, deleted in another tab), which the re-read turns into
 *   the page's not-found state.
 * - `SocialAccount` — the connected accounts list; read-only here.
 */
export const smmApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['SocialPost', 'SocialAccount'],
  endpoints: {
    [cfg.listSocialPosts.generated]: {
      providesTags: (result) => provideListTags(selectSocialPosts(result), 'SocialPost'),
    },
    [cfg.getSocialPost.generated]: {
      providesTags: (_result, _error, arg) => [postTag(arg.id)],
    },
    [cfg.createSocialPost.generated]: {
      invalidatesTags: [POST_LIST_TAG],
    },
    [cfg.updateSocialPost.generated]: { invalidatesTags: invalidatePostAndList },
    [cfg.deleteSocialPost.generated]: { invalidatesTags: [POST_LIST_TAG] },
    [cfg.regenerateSocialPost.generated]: { invalidatesTags: invalidatePostAndList },
    // All three answer with the post in its new status, written into its cache
    // entry so the page shows that status — and only the actions of that status —
    // without waiting for the refetch.
    [cfg.approveSocialPost.generated]: {
      invalidatesTags: invalidatePostAndList,
      onQueryStarted: upsertPostOnSettled,
    },
    [cfg.unscheduleSocialPost.generated]: {
      invalidatesTags: invalidatePostAndList,
      onQueryStarted: upsertPostOnSettled,
    },
    [cfg.redraftSocialPost.generated]: {
      invalidatesTags: invalidatePostAndList,
      onQueryStarted: upsertPostOnSettled,
    },
    // Answers with the UNPUBLISHED post, written into its cache entry like the
    // three above. A 503 (`SERVICE_UNAVAILABLE_UNPUBLISH`) answers without one,
    // having taken some copies down already: the invalidation, which runs on a
    // failure too, re-reads which copies are still live.
    [cfg.unpublishSocialPost.generated]: {
      invalidatesTags: invalidatePostAndList,
      onQueryStarted: upsertPostOnSettled,
    },
    // A cancelled post stays readable (status CANCELLED), so refetch it too.
    [cfg.cancelSocialPost.generated]: { invalidatesTags: invalidatePostAndList },
    [cfg.presignSocialPostMedia.generated]: {
      invalidatesTags: (result, error, arg) =>
        isSmmStatusRefused(error) || getSmmError(error).status === 404
          ? invalidatePostAndList(result, error, arg)
          : [],
    },
    [cfg.registerSocialPostMedia.generated]: { invalidatesTags: invalidatePostAndList },
    [cfg.removeSocialPostMedia.generated]: { invalidatesTags: invalidatePostAndList },
    [cfg.listSocialAccounts.generated]: {
      providesTags: (result) => provideListTags(result, 'SocialAccount'),
    },
  },
});

// Re-export hooks with cleaner names via endpoint definitions
export const hookExports: HookExports<typeof cfg, typeof smmApi> = createHookExports(cfg, smmApi);

// Re-export endpoints object with cleaner names for direct usage
export const smmApiEndpoints = createEndpointExports(cfg, smmApi);
